import { format } from "date-fns";
import type { Locale } from "date-fns/locale";
import { fr } from "date-fns/locale";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { normalizeCompanyTimeZone } from "./companyTimeZone";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export const formatDate = (date?: string | Date) => {
  if (!date) return "N/A";

  const parsedDate = typeof date === "string" ? new Date(date) : date;

  if (isNaN(parsedDate.getTime()) || parsedDate.getFullYear() === 1) {
    return "N/A";
  }

  return format(parsedDate, "dd/MM/yyyy HH:mm:ss");
};

export const formatDateWithLocale = (date?: string | Date, locale: Locale = fr) => {
  if (!date) return "N/A";

  const parsedDate = typeof date === "string" ? new Date(date) : date;

  if (isNaN(parsedDate.getTime()) || parsedDate.getFullYear() === 1) {
    return "N/A";
  }

  return format(parsedDate, "dd/MM/yyyy", { locale });
};

/** Long French month labels; index 0 = January (aligned with `Date#getMonth()`). */
export const MONTH_NAMES_FR = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
] as const;

/** @param month 1–12 (calendar month). */
export const formatMonth = (month: number) => MONTH_NAMES_FR[month - 1];

export const formatBirthdate = (date?: string | Date): string => {
  if (!date) return "";

  const parsedDate = typeof date === "string" ? new Date(date) : date;

  if (isNaN(parsedDate.getTime()) || parsedDate.getFullYear() === 1) {
    return "";
  }

  return format(date, "dd/MM/yyyy");
};

// -------------------- LEAVE MANAGEMENT UTILITIES --------------------

/**
 * Convert YYYY-MM-DD date string to ISO 8601 format with browser timezone
 * Used for API calls that require full ISO date format
 */
export const convertToISO8601 = (dateString: string): string => {
  const date = new Date(dateString + "T00:00:00");
  return date.toISOString();
};

/**
 * Return RFC3339 with UTC midnight for a given Date
 * Example: 2025-08-12T00:00:00Z
 */
export const toUTCZeroString = (date: Date): string => {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth() + 1; // 1-12
  const d = date.getUTCDate();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${y}-${pad(m)}-${pad(d)}T00:00:00Z`;
};

/** Local calendar YYYY-MM-DD (not `toISOString()` UTC day). Use for API `from`/`to` date ranges. */
export function toLocalDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Convert a YYYY-MM-DD string to RFC3339 at UTC midnight
 * Example: '2025-08-12' -> '2025-08-12T00:00:00Z'
 */
export const ymdToRFC3339UTC = (dateStr: string): string => {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map((v) => parseInt(v, 10));
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${String(y).padStart(4, "0")}-${pad(m)}-${pad(d)}T00:00:00Z`;
};

/** YYYY-MM-DD at start of calendar day in the company IANA zone, as UTC ISO string. */
export function ymdToRFC3339InTimeZone(dateStr: string, ianaTimeZone?: string | null): string {
  if (!dateStr) return "";
  const tz = normalizeCompanyTimeZone(ianaTimeZone);
  const d = dayjs.tz(`${dateStr} 00:00:00`, "YYYY-MM-DD HH:mm:ss", tz);
  if (!d.isValid()) return ymdToRFC3339UTC(dateStr);
  return d.utc().toISOString();
}

/** Wall clock (HH:mm) on a calendar day in company zone → UTC ISO instant. */
export function wallDateTimeInTimeZoneToIsoUtc(
  dateKey: string,
  hm: string,
  ianaTimeZone?: string | null,
): string {
  const tz = normalizeCompanyTimeZone(ianaTimeZone);
  const parts = hm.split(":");
  const h = parseInt(parts[0] ?? "", 10);
  const m = parseInt(parts[1] ?? "", 10);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return "";
  const hh = String(h).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  const d = dayjs.tz(`${dateKey} ${hh}:${mm}:00`, "YYYY-MM-DD HH:mm:ss", tz);
  if (!d.isValid()) return "";
  return d.utc().toISOString();
}

/** Encode a YYYY-MM-DD + HH:mm wall clock as an ISO UTC string with no TZ shift (TZ-agnostic data, e.g. leaves). */
export function wallDateTimeToIsoUtc(dateKey: string, hm: string): string {
  if (!dateKey) return "";

  const parts = hm.split(":");
  const h = parseInt(parts[0] ?? "", 10);
  const m = parseInt(parts[1] ?? "", 10);

  if (!Number.isFinite(h) || !Number.isFinite(m)) return "";

  const hh = String(h).padStart(2, "0");
  const mm = String(m).padStart(2, "0");

  return `${dateKey}T${hh}:${mm}:00Z`;
}

/**
 * Get today's date in YYYY-MM-DD format for date inputs
 */
export const getTodayDateString = (): string => {
  return new Date().toISOString().split("T")[0];
};

export function getTodayDateStringInTimeZone(ianaTimeZone?: string | null): string {
  const tz = normalizeCompanyTimeZone(ianaTimeZone);
  return dayjs().tz(tz).format("YYYY-MM-DD");
}

/**
 * Calculate working days between two dates (Monday to Saturday, excluding Sundays and May 1st)
 * Used for leave request day calculation
 */
export const calculateWorkingDays = (startDate: Date, endDate: Date): number => {
  let count = 0;
  const current = new Date(startDate);

  while (current <= endDate) {
    const dayOfWeek = current.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const isWeekend = dayOfWeek === 0; // Only Sunday is excluded
    const isMay1st = current.getMonth() === 4 && current.getDate() === 1; // May 1st

    if (!isWeekend && !isMay1st) {
      count++;
    }

    current.setDate(current.getDate() + 1);
  }

  return count;
};

/** Same rules as calculateWorkingDays, but YYYY-MM-DD bounds are interpreted in the company IANA zone. */
export function calculateWorkingDaysFromYmdInTimeZone(
  startKey: string,
  endKey: string,
  ianaTimeZone?: string | null,
): number {
  const tz = normalizeCompanyTimeZone(ianaTimeZone);
  let current = dayjs.tz(`${startKey} 00:00:00`, "YYYY-MM-DD HH:mm:ss", tz);
  const end = dayjs.tz(`${endKey} 00:00:00`, "YYYY-MM-DD HH:mm:ss", tz);
  if (!current.isValid() || !end.isValid()) return 0;
  let count = 0;
  while (current.isBefore(end) || current.isSame(end, "day")) {
    const dayOfWeek = current.day();
    const isWeekend = dayOfWeek === 0;
    const isMay1st = current.month() === 4 && current.date() === 1;
    if (!isWeekend && !isMay1st) {
      count++;
    }
    current = current.add(1, "day");
  }
  return count;
}

/**
 * Get the start of week (Monday) for a given date
 */
export const getStartOfWeek = (date: Date): Date => {
  const start = new Date(date);
  const day = start.getDay();
  const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Monday as first day
  return new Date(start.setDate(diff));
};

/**
 * Get the end of week (Sunday) for a given date
 */
export const getEndOfWeek = (date: Date): Date => {
  const end = getStartOfWeek(date);
  end.setDate(end.getDate() + 6);
  return end;
};
